/**
 * DEMOS de OPUS ci — datos de UN modelo IFC leídos en el navegador, en la forma que usan los demos.
 *
 * Todo sale del modelo: la medición es la MISMA de ci (`lib/medicionIfc.ts`, no se reimplementa) y lo
 * demás (GlobalId, clase, nivel, tipo, material, cantidades propias) se lee de las relaciones del
 * estándar IFC. Nada de listas de nombres.
 */
import type { NivelIfc } from '../../lib/medicionIfc.js';

/**
 * Clases del estándar IFC que NO son obra que se construya ni se presupueste: espacios (IfcSpace, el aire de un
 * cuarto), vanos (IfcOpeningElement, el hueco de una ventana), elementos virtuales (límites sin materia)
 * y anotaciones (textos, cotas). Es la taxonomía FIJA del formato (ISO 16739), no un dato de obra.
 */
export const CLASES_QUE_NO_SE_CONSTRUYEN = [
  'IfcSpace',
  'IfcOpeningElement',
  'IfcVirtualElement',
  'IfcAnnotation',
] as const;

export interface CajaDemo {
  min: [number, number, number];
  max: [number, number, number];
}

export interface ElementoDemo {
  /** `${fuente}:${expressID}` — único aunque haya dos modelos en la escena. */
  clave: string;
  fuente: string;
  eid: number;
  /** GlobalId del IFC: la identidad estable entre revisiones. */
  gid: string;
  /** Clase IFC ("IfcWallStandardCase"). */
  clase: string;
  nombre: string;
  /** Tipo del modelo (la "capa" del inventario de ci). */
  tipo: string;
  /** PredefinedType del elemento o, si no dice, el de su tipo. */
  predef: string;
  nivelId: number | null;
  nivelNombre: string | null;
  /** 0 = el nivel más bajo (por elevación). */
  nivelOrden: number | null;
  /** Medidas en m, m², m³ (las de ci: declaradas contrastadas con la geometría, o estimadas). */
  area?: number;
  longitud?: number;
  volumen?: number;
  /** true si el modelo no declaraba cantidades y se midieron del dibujo. */
  estimado: boolean;
  /** Lo que mide la malla de la pieza (la referencia de ci), para lo que el modelo no declara. */
  geo: { area?: number; longitud?: number; volumen?: number };
  tieneCantidadesPropias: boolean;
  tieneTipo: boolean;
  tieneMaterial: boolean;
  /** Puertas y ventanas que llenan los vanos de esta pieza (expressID), según el modelo. */
  alojados: number[];
  /** Caja en coordenadas del mundo del visor (Y hacia arriba). null si no tiene geometría. */
  caja: CajaDemo | null;
}

export interface ModeloDemo {
  fuente: string;
  /** Solo los elementos constructivos (clases que ci inventaría). */
  elementos: ElementoDemo[];
  porClave: Map<string, ElementoDemo>;
  niveles: NivelIfc[];
  /** Conteo de lo que el modelo trae y no se construye, por clase del estándar. */
  noSeConstruye: { clase: string; cantidad: number }[];
  /** Cuartos del modelo (IfcSpace): su nombre largo ("Baño") o, si no trae, su nombre corto. */
  espacios: string[];
}

const enumVal = (x: any): string => (typeof x === 'string' ? x : (x?.value ?? '')).toString().toUpperCase();

/** Lee relaciones del modelo (sin geometría). La medición la aporta el visor con `crearMedidor`. */
export function leerRelaciones(api: any, WebIFC: any, modelID: number) {
  const idsDe = (code: number | undefined): number[] => {
    const out: number[] = [];
    if (code == null) return out;
    try {
      const v = api.GetLineIDsWithType(modelID, code);
      for (let i = 0; i < v.size(); i++) out.push(v.get(i));
    } catch {
      /* clase ausente en el esquema */
    }
    return out;
  };
  const linea = (id: number | undefined): any => {
    if (id == null) return null;
    try {
      return api.GetLine(modelID, id);
    } catch {
      return null;
    }
  };

  const claseDe = new Map<number, string>();
  // Todo IfcElement con su clase (el esquema de web-ifc dice la herencia), como el inventario de ci.
  try {
    const v = api.GetLineIDsWithType(modelID, WebIFC.IFCELEMENT, true);
    for (let i = 0; i < v.size(); i++) {
      const id = v.get(i);
      claseDe.set(id, String(api.GetNameFromTypeCode(api.GetLineType(modelID, id))));
    }
  } catch {
    /* modelo sin elementos */
  }

  const tipoDe = new Map<number, number>();
  for (const rid of idsDe(WebIFC.IFCRELDEFINESBYTYPE)) {
    const rel = linea(rid);
    const t = rel?.RelatingType?.value;
    for (const o of rel?.RelatedObjects ?? []) if (o?.value != null && t != null) tipoDe.set(o.value, t);
  }
  const conMaterial = new Set<number>();
  for (const rid of idsDe(WebIFC.IFCRELASSOCIATESMATERIAL)) {
    const rel = linea(rid);
    for (const o of rel?.RelatedObjects ?? []) if (o?.value != null) conMaterial.add(o.value);
  }
  const conCantidades = new Set<number>();
  for (const rid of idsDe(WebIFC.IFCRELDEFINESBYPROPERTIES)) {
    const rel = linea(rid);
    const defId = rel?.RelatingPropertyDefinition?.value;
    if (defId == null) continue;
    let t: number | null = null;
    try {
      t = api.GetLineType(modelID, defId);
    } catch {
      t = null;
    }
    if (t !== WebIFC.IFCELEMENTQUANTITY) continue;
    const q = linea(defId);
    if (!(q?.Quantities?.length > 0)) continue;
    for (const o of rel?.RelatedObjects ?? []) if (o?.value != null) conCantidades.add(o.value);
  }

  const noSeConstruye = CLASES_QUE_NO_SE_CONSTRUYEN.map((clase) => ({
    clase,
    cantidad: idsDe(WebIFC[clase.toUpperCase()]).length,
  })).filter((x) => x.cantidad > 0);

  function datosDe(eid: number) {
    const l = linea(eid);
    const tId = tipoDe.get(eid);
    let predef = enumVal(l?.PredefinedType);
    if (!predef || predef === 'NOTDEFINED' || predef === 'USERDEFINED') {
      const pt = enumVal(linea(tId)?.PredefinedType);
      if (pt) predef = pt;
    }
    return {
      gid: (l?.GlobalId?.value ?? '').toString(),
      nombre: (l?.Name?.value ?? '').toString().trim(),
      predef,
      tieneTipo: tId != null,
      tieneMaterial: conMaterial.has(eid) || (tId != null && conMaterial.has(tId)),
      tieneCantidadesPropias: conCantidades.has(eid),
      alojados: alojadosDe.get(eid) ?? [],
    };
  }

  // Vanos: muro → vano (IfcRelVoidsElement) → puerta/ventana que lo llena (IfcRelFillsElement).
  const llenaVano = new Map<number, number[]>();
  for (const rid of idsDe(WebIFC.IFCRELFILLSELEMENT)) {
    const rel = linea(rid);
    const v = rel?.RelatingOpeningElement?.value;
    const e = rel?.RelatedBuildingElement?.value;
    if (v != null && e != null) (llenaVano.get(v) ?? llenaVano.set(v, []).get(v)!).push(e);
  }
  const alojadosDe = new Map<number, number[]>();
  for (const rid of idsDe(WebIFC.IFCRELVOIDSELEMENT)) {
    const rel = linea(rid);
    const m = rel?.RelatingBuildingElement?.value;
    const v = rel?.RelatedOpeningElement?.value;
    if (m == null || v == null) continue;
    for (const e of llenaVano.get(v) ?? []) (alojadosDe.get(m) ?? alojadosDe.set(m, []).get(m)!).push(e);
  }

  const espacios: string[] = [];
  for (const id of idsDe(WebIFC.IFCSPACE)) {
    const l = linea(id);
    const nm = ((l?.LongName?.value ?? '') || (l?.Name?.value ?? '')).toString().trim();
    if (nm) espacios.push(nm);
  }

  return { claseDe, datosDe, noSeConstruye, espacios };
}
